import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/presentation/hooks/useAuth";
import { Button } from "../../../../ui/components/Button";
import { Badge } from "../../../../ui/components/Badge";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { API_BASE_URL } from "../../../../config/api";
import {
  FeatherLogOut,
  FeatherBuilding,
  FeatherArrowRight,
} from "@subframe/core";

interface UserCompany {
  company: {
    id: number;
    company_name: string;
    app_url: string;
  };
  role_name?: string;
  is_company_admin?: boolean;
}

export const CompanySelectionPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userCompanies, setUserCompanies] = useState<UserCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadUserCompanies();
    }
  }, [user]);

  const loadUserCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${API_BASE_URL}/api/company-selection/list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to load companies");
      }

      const data = await response.json();
      setUserCompanies(data.data?.companies || []);
    } catch (err: any) {
      setError(err.message || "Failed to load companies");
      console.error("Failed to load user companies:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterCompany = async (
    companyId: number,
    companyAppUrl: string,
  ) => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${API_BASE_URL}/api/company-selection/enter`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ company_id: companyId }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || "Failed to enter company dashboard");
        return;
      }

      const data = await response.json();

      // Store master admin URL for logout redirect
      const masterAdminUrl = window.location.origin;

      // Pass token and company_id as URL parameters
      // Also pass master_admin_url for logout redirect
      const params = new URLSearchParams({
        token: data.data.token,
        company_id: companyId.toString(),
        master_admin_url: masterAdminUrl,
      });

      // Redirect to tenant dashboard with token in URL
      // window.location.href = `https://${companyAppUrl}.zournalhq.com/from-master?${params.toString()}`;
      window.location.href = `${companyAppUrl}/from-master?${params.toString()}`;
      // window.location.href = `http://localhost:3000/from-master?${params.toString()}`;
    } catch (err: any) {
      alert(err.message || "Failed to enter company dashboard");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Header Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Select Company
              </span>
              <span className="text-body font-body text-subtext-color">
                Choose a company to access its dashboard
              </span>
            </div>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherLogOut />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Company List */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 p-8 overflow-auto">
          {error && (
            <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <span className="text-sm font-medium text-red-800">{error}</span>
            </div>
          )}

          {userCompanies.length === 0 && !loading ? (
            <div className="flex w-full flex-col items-center justify-center gap-4 p-12 rounded-lg border border-solid border-neutral-border bg-default-background">
              <FeatherBuilding className="h-12 w-12 text-subtext-color" />
              <span className="text-heading-3 font-heading-3 text-default-font">
                No Companies Assigned
              </span>
              <span className="text-body font-body text-subtext-color">
                You haven't been assigned to any companies yet.
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
              {userCompanies.map((item) => (
                <div
                  key={item.company.id}
                  className="flex flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6 hover:border-brand-300 transition-colors"
                >
                  <div className="flex w-full items-start justify-between">
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        {item.company.company_name}
                      </span>
                      <span className="text-body font-body text-subtext-color">
                        {item.company.app_url}
                      </span>
                    </div>
                    {item.is_company_admin && (
                      <Badge variant="brand">Admin</Badge>
                    )}
                  </div>

                  {item.role_name && (
                    <Badge variant="neutral">{item.role_name}</Badge>
                  )}

                  <Button
                    variant="brand-primary"
                    className="w-full"
                    iconRight={<FeatherArrowRight />}
                    onClick={() =>
                      handleEnterCompany(item.company.id, item.company.app_url)
                    }
                    disabled={!item.role_name}
                    title={
                      !item.role_name
                        ? "You do not have a role assigned for this company. Please contact the company admin."
                        : ""
                    }
                  >
                    {item.role_name ? "Enter Dashboard" : "Role Not Assigned"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DefaultPageLayout>
  );
};
